import React from 'react';
import { Field } from 'redux-form';
import cn from 'classnames';
import get from 'lodash/get';
import PropTypes from 'prop-types';
import Icon from 'chameleon/ui-stack/ui/base/icon';

import './styles.scss';

const colorsByProp = {
  blue: {
    isChecked: {
      false: '!border-gray-700',
      true: '!bg-blue-700 !border-blue-700',
    },
  },
};

export const CheckBox = ({
  className,
  field,
  onClick,
  isChecked,
  isDisabled,
  isCollapsed = false,
  isCollapsible = false,
  onCollapseExpand,
  collapseKey,
  children,
  isActiveItem,
  isClickDisabled,
  isInvisible,
  dataTestId,
  isHiddenCheckbox,
  isDropdownIconInvisible,
  tabIndex = null,
  color = null,
}) => (
  <div
    data-test-id={dataTestId}
    className={cn('checkbox', className, {
      'checkbox--inactive': !isActiveItem,
    })}
    role="checkbox"
    aria-checked={isChecked}
    data-test-checkbox
    tabIndex={
      tabIndex === null
        ? isDisabled || isClickDisabled
          ? null
          : '0'
        : tabIndex
    }
    onClick={(event) => {
      const clickInCheckbox =
        event.target.parentNode.classList.contains('custom-checkbox') ||
        event.target.classList.contains('custom-checkbox');

      if (isHiddenCheckbox) {
        onClick(event, field);
        onCollapseExpand && onCollapseExpand(collapseKey, event);
      } else if (clickInCheckbox) {
        onClick(event, field);
      } else {
        onCollapseExpand && onCollapseExpand(collapseKey, event);
      }
    }}
    onKeyDown={(event) => {
      const clickInCheckbox =
        event.target.parentNode.classList.contains('custom-checkbox') ||
        event.target.classList.contains('custom-checkbox');

      if (!isDisabled && (event.keyCode === 32 || event.keyCode === 13)) {
        if (isHiddenCheckbox) {
          onClick(event, field);
          event.stopPropagation();
          event.preventDefault();
          onCollapseExpand && onCollapseExpand(collapseKey, event);
        } else if (clickInCheckbox) {
          onClick(event, field);
          event.stopPropagation();
          event.preventDefault();
        } else {
          onCollapseExpand && onCollapseExpand(collapseKey, event);
        }
      }
    }}
  >
    <div
      className={cn(
        'custom-checkbox',
        {
          'is-checked': isChecked,
          'is-disabled': isDisabled,
          invisible: isInvisible,
        },
        isHiddenCheckbox ? 'hidden' : 'flex',
        get(colorsByProp, [color, 'isChecked', isChecked]),
      )}
    >
      <Icon id="checkmark_sm" />
    </div>
    {isActiveItem && (
      <div className="checkmark-icon-wrapper pr-2">
        <Icon id="checkmark_sm" />
      </div>
    )}
    <div className="name ellipsis text-14">{children}</div>
    {isCollapsible && (
      <div
        className={cn(
          'flex-1 text-right ml-0.8 mr-4.2 expand-collapse-button',
          {
            invisible: isDropdownIconInvisible,
          },
        )}
        tabIndex={tabIndex === null ? '0' : tabIndex}
        role="button"
        onClick={(e) => onCollapseExpand && onCollapseExpand(collapseKey, e)}
        onKeyDown={(e) => {
          if (onCollapseExpand && (e.keyCode === 32 || e.keyCode === 13)) {
            onCollapseExpand(collapseKey, e);
            e.stopPropagation();
            e.preventDefault();
          }
        }}
      >
        {isCollapsed ? (
          <Icon id="keyboard_arrow_down" width={8} />
        ) : (
          <Icon id="keyboard_arrow_up" width={8} />
        )}
      </div>
    )}
  </div>
);

export const CheckboxItem = ({
  id,
  name,
  className,
  fields,
  section,
  onToggle,
  isChecked,
  selection,
  fieldIndex,
  isFinalExam,
  handleSelection,
  isMultipleSelection,
  fieldName,
  isDisabled: isCheckBoxItemDisable,
  onNameClick,
  toggleOnlyOnActive,
  isClickDisabled,
  isInvisible,
  toggleOnNameClick,
  dataTestId,
  ...rest
}) => {
  let isDisabled;
  if (isFinalExam && isMultipleSelection) {
    isDisabled = !isChecked && selection.length === 4;
  } else {
    isDisabled = isCheckBoxItemDisable || (selection && selection !== id);
  }

  const handleNameAction = (event) => {
    // Check for keyCode in case of a keyDown event, otherwise is a click event.
    const isCorrectEvent = event.keyCode
      ? [13, 32].includes(event.keyCode)
      : true;

    if (isCorrectEvent && onNameClick && !isDisabled && !isClickDisabled) {
      event.preventDefault();
      event.stopPropagation();

      if (toggleOnNameClick) {
        const shouldToggle =
          !toggleOnlyOnActive || (toggleOnlyOnActive && rest.isActiveItem);

        if (shouldToggle) {
          onNameClick(
            event,
            fields,
            fieldIndex,
            isChecked,
            section,
            !!onNameClick,
          );
        }
      } else {
        onNameClick(id, event);
      }
    }
  };

  let divNameProps = {};
  if (onNameClick) {
    divNameProps = {
      onClick: handleNameAction,
      onKeyDown: handleNameAction,
    };
  }

  return (
    <Field
      name={fieldName || 'assignee'}
      key={id}
      component={CheckBox}
      props={{
        className: cn(`section ${isDisabled ? 'disabled' : ''}`, className),
        onClick: (event) => {
          if (!isDisabled && !isClickDisabled) {
            handleSelection(id, section.sectionId, section.assignmentId);

            const shouldToggle =
              !toggleOnlyOnActive || (toggleOnlyOnActive && rest.isActiveItem);

            if (shouldToggle) {
              onToggle(
                event,
                fields,
                fieldIndex,
                isChecked,
                section,
                !!onNameClick,
              );
            }
          } else {
            return false;
          }
        },
        name,
        isChecked,
        isDisabled,
        isClickDisabled,
        isInvisible,
        ...rest,
      }}
    >
      <div data-test-id={dataTestId} {...divNameProps}>
        {name}
      </div>
    </Field>
  );
};

const CheckBoxList = ({
  sections,
  onToggle,
  selection,
  setSelection,
  isFinalExam,
  isMultipleSelection = false,
  ...other
}) => {
  const { fields } = other;
  const allFields = fields.getAll() || [];

  return sections.map((section) => {
    const { id, name } = section;
    const fieldIndex = allFields.findIndex((field) => field.id === id);
    const isChecked = fieldIndex >= 0;

    const handleSelection = (elementId) => {
      if (isFinalExam) {
        setSelection(
          !(selection && selection === elementId) ? elementId : null,
        );
      } else {
        return false;
      }
    };

    const checkboxProps = {
      id,
      name,
      fields,
      section,
      onToggle,
      isChecked,
      selection,
      fieldIndex,
      isFinalExam,
      handleSelection,
      isMultipleSelection,
    };

    return <CheckboxItem key={id} {...checkboxProps} />;
  });
};

CheckBoxList.propTypes = {
  setSelection: PropTypes.func,
  selection: PropTypes.any,
};

CheckBoxList.defaultProps = {
  setSelection: () => {},
  selection: 0,
};

export default CheckBoxList;
